"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { motion } from "framer-motion";
import Image from "@/helpers/Image";
import {
  PageEcommerceSchema,
  PageEcommerceProcessStep,
} from "@/sanity/pages/PageEcommerceTemplate";
import dots from "@/public/ecommerce/processcards.png";
const RATING_DOTS = [0.1, 0.4, 0.6, 0.8, 1];
const ease = [0.25, 0.1, 0.25, 1] as const;

function StepCard({ step }: { step: PageEcommerceProcessStep }) {
  return (
    <div className="relative overflow-hidden rounded-[0.25rem] border border-white/10 bg-white/[0.04] pl-[4.5rem] pr-[4rem] py-[3.5rem] flex flex-col gap-[3rem]">
      <Image
        src={dots}
        alt=""
        className="absolute inset-0 !w-full !h-full object-cover"
      />
      <div className="absolute left-0 top-0 bottom-0 w-[0.375rem] bg-[#12f0b4]" />

      {step.icon && (
        <Image
          src={step.icon}
          alt=""
          className="!w-[5.25rem] !h-[5.25rem] object-contain"
        />
      )}

      <div className="flex flex-col gap-[1.75rem] lg:max-w-[56.5rem]">
        <p className="mb-0 font-medium text-[3rem] leading-[1.3] tracking-[-0.09rem] text-white">
          {step.title}
        </p>
        <p className="mb-0 text-[2rem] leading-[1.6] text-white/70">
          {step.body}
        </p>
      </div>
    </div>
  );
}

export default function ProcessSection({
  data,
}: {
  data: PageEcommerceSchema;
}) {
  if (!data.processTitle && !data.processSteps?.length) return null;

  const steps = data.processSteps ?? [];

  return (
    <section className="w-full bg-[#001b14] pt-[7rem] lg:py-[16rem]">
      <div className="mx-auto w-[min(150rem,100%)] px-[4rem] flex flex-col lg:flex-row lg:justify-between gap-[8rem] lg:gap-[14rem] items-start">
        {/* Left — header */}
        <div className="flex flex-col gap-[3rem] lg:sticky lg:top-[15rem] lg:max-w-[43.375rem] shrink-0">
          <div className="flex flex-col items-center lg:items-start">
            {data.processChip && (
              <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-white">
                {data.processChip}
              </p>
            )}

            {data.processTitle && (
              <div className="richtext lg:richtext-start prose-headings:mb-[2rem] prose-headings:text-[5rem] lg:prose-headings:text-[6rem] prose-headings:leading-[1.15] prose-headings:tracking-[-0.3rem] prose-headings:text-white prose-p:text-white/70 prose-p:px-[2rem] lg:prose-p:px-0">
                <PortableText
                  value={data.processTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          <div className="w-full h-px bg-white/10" />

          <div className="flex flex-col items-center lg:items-start gap-[1.5rem]">
            <div className="flex items-center gap-[1.5rem]">
              <div className="flex items-center gap-[0.875rem]">
                {RATING_DOTS.map((opacity, i) => (
                  <span
                    key={i}
                    className="inline-block w-[0.75rem] h-[0.75rem] bg-white"
                    style={{ opacity }}
                  />
                ))}
              </div>
              {data.processRating && (
                <span className="font-mono text-[2rem] font-semibold text-white/70">
                  {data.processRating}
                </span>
              )}
            </div>

            {data.processSocialProof && (
              <p className="mb-0 text-[2rem] leading-[1.33] text-white/70 text-center lg:text-start">
                {data.processSocialProof}
              </p>
            )}
          </div>

          {data.processButton && (
            <CTAButton
              data={data.processButton}
              overrideVariant="white"
              className="self-start w-full lg:w-full"
            />
          )}
        </div>

        {/* Right — step cards */}
        <div className="flex-1 flex flex-col gap-[2rem] min-w-0 relative lg:max-w-[76.375rem]">
          {steps.map((step, i) => (
            <motion.div
              key={step._key ?? i}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, ease, delay: i * 0.12 }}
            >
              <StepCard step={step} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
